import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getApiErrorMessage, handleApiCall } from '../../apis';
import { endpoint } from '../../utils/endpoints';
import Toastify, { notifyError } from '../../utils/tostfy';
import EyeIcon from '../../assets/image/eye-icon.svg';
import LeftAngle from '../../assets/image/left-angle.svg';
import RightAngle from '../../assets/image/right-angle.svg';
import FilterIcon from '../../assets/image/filter.svg';
import DateRangeFilter from '../../Components/DateRangeFilter/DateRangeFilter';
import DateRangeAppliedLabel from '../../Components/DateRangeFilter/DateRangeAppliedLabel';
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../utils/search";
import { formatTripAmount as formatFuelAmount } from '../../utils/formatAmount';
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.data)) return value.data;
    return [];
};

const mapLocation = (item) => ({
    id: item?._id || item?.id || '',
    name: item?.name || item?.location_name || '—',
});

const mapFuelPumpOption = (item) => ({
    id: item?._id || item?.id || '',
    name: item?.fuel_pump_name || item?.name || 'Fuel pump',
});

const mapSettlementRow = (item) => ({
    id: item?._id || item?.id || '',
    fuelPumpName: item?.fuel_pump_name || item?.name || '-',
    miplLocation:
        (typeof item?.mipl_location_id === 'object' && item?.mipl_location_id?.name) ||
        item?.location_name ||
        '-',
});

const filterByQuery = (items, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.name.toLowerCase().includes(q));
};

const FilterChevron = ({ open }) => (
    <span
        className={`inline-block text-gray-400 transition-transform duration-200 ${open ? '-rotate-90' : ''}`}
        aria-hidden
    >
        ›
    </span>
);

const FuelPumpManagement = () => {
    const filterRef = useRef(null);

    const [rows, setRows] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [stats, setStats] = useState({
        totalVehicle: 0,
        totalFuel: '-',
        totalAmount: '-',
    });
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [filterOpen, setFilterOpen] = useState(false);
    const [filterSub, setFilterSub] = useState(null);
    const [miplLocationId, setMiplLocationId] = useState('');
    const [fuelPumpId, setFuelPumpId] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [appliedMiplLocationId, setAppliedMiplLocationId] = useState('');
    const [appliedFuelPumpId, setAppliedFuelPumpId] = useState('');
    const [appliedDateFrom, setAppliedDateFrom] = useState('');
    const [appliedDateTo, setAppliedDateTo] = useState('');

    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [locationSearch, setLocationSearch] = useState('');

    const [fuelPumps, setFuelPumps] = useState([]);
    const [pumpsLoading, setPumpsLoading] = useState(false);
    const [pumpSearch, setPumpSearch] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    useLayoutEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, appliedMiplLocationId, appliedFuelPumpId, appliedDateFrom, appliedDateTo]);

    useEffect(() => {
        const onDoc = (e) => {
            if (!filterOpen) return;
            const el = filterRef.current;
            if (el && !el.contains(e.target)) {
                setFilterOpen(false);
                setFilterSub(null);
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [filterOpen]);

    useEffect(() => {
        if (!filterOpen || filterSub !== 'location') return;
        let cancelled = false;
        const loadLocations = async () => {
            setLocationsLoading(true);
            try {
                const res = await handleApiCall('get', endpoint.listOfAlllocation, {});
                if (cancelled) return;
                if (res?.data?.code === 200) {
                    const d = res?.data?.data;
                    const raw = Array.isArray(d) ? d : toArray(d?.location_data ?? d?.locations);
                    setLocations(raw.map(mapLocation).filter((x) => x.id));
                } else {
                    notifyError(getApiErrorMessage(res, 'Failed to load locations'));
                    setLocations([]);
                }
            } catch {
                if (!cancelled) {
                    notifyError('Failed to load locations');
                    setLocations([]);
                }
            } finally {
                if (!cancelled) setLocationsLoading(false);
            }
        };
        loadLocations();
        return () => {
            cancelled = true;
        };
    }, [filterOpen, filterSub]);

    useEffect(() => {
        if (!filterOpen || filterSub !== 'pump') return;
        const locForPumps = miplLocationId || appliedMiplLocationId;
        if (!locForPumps) {
            setFuelPumps([]);
            return;
        }

        let cancelled = false;
        const loadPumps = async () => {
            setPumpsLoading(true);
            try {
                const res = await handleApiCall(
                    'get',
                    `${endpoint.listOfAllFuelPump}?mipl_location_id=${encodeURIComponent(locForPumps)}`,
                    {}
                );
                if (cancelled) return;
                if (res?.data?.code === 200) {
                    const d = res?.data?.data;
                    const raw =
                        (Array.isArray(d) ? d : null) ||
                        toArray(d?.fuel_pump_data) ||
                        toArray(d?.fuel_pumps) ||
                        [];
                    setFuelPumps(raw.map(mapFuelPumpOption).filter((x) => x.id));
                } else {
                    notifyError(getApiErrorMessage(res, 'Failed to load fuel pumps'));
                    setFuelPumps([]);
                }
            } catch {
                if (!cancelled) {
                    notifyError('Failed to load fuel pumps');
                    setFuelPumps([]);
                }
            } finally {
                if (!cancelled) setPumpsLoading(false);
            }
        };
        loadPumps();
        return () => {
            cancelled = true;
        };
    }, [filterOpen, filterSub, miplLocationId, appliedMiplLocationId]);

    const fetchFuelSettlementList = async () => {
        setListLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(currentPage),
                limit: String(itemsPerPage),
            });
            const q = clampSearchInput(debouncedSearch).trim();
            if (q) params.set('search', q);
            if (appliedMiplLocationId) params.set('mipl_location_id', appliedMiplLocationId);
            if (appliedFuelPumpId) params.set('fuel_pump_id', appliedFuelPumpId);
            if (appliedDateFrom) params.set('from_date', appliedDateFrom);
            if (appliedDateTo) params.set('to_date', appliedDateTo);

            const response = await handleApiCall(
                'get',
                `${endpoint.listOfFuelSettlement}?${params.toString()}`,
                {}
            );
            if (response?.data?.code === 200) {
                const d = response?.data?.data || {};
                const raw = Array.isArray(d.fuel_settlement_data) ? d.fuel_settlement_data : [];
                setRows(raw.map(mapSettlementRow).filter((row) => row.id));
                setStats({
                    totalVehicle: d.total_vehicle ?? 0,
                    totalFuel: d.total_fuel ?? '-',
                    totalAmount: formatFuelAmount(d.total_amount),
                });
                setTotalRecords(d.total ?? raw.length);
                setTotalPages(Math.max(1, d.total_pages ?? 1));
            } else {
                notifyError(getApiErrorMessage(response, 'Failed to load fuel settlement list'));
                setRows([]);
                setTotalRecords(0);
                setTotalPages(1);
            }
        } catch {
            notifyError('Failed to load fuel settlement list');
            setRows([]);
            setTotalRecords(0);
            setTotalPages(1);
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        fetchFuelSettlementList();
    }, [
        debouncedSearch,
        currentPage,
        itemsPerPage,
        appliedMiplLocationId,
        appliedFuelPumpId,
        appliedDateFrom,
        appliedDateTo,
    ]);

    const hasActiveFilters = Boolean(
        appliedMiplLocationId || appliedFuelPumpId || appliedDateFrom || appliedDateTo
    );
    const hasPartialDateRange = Boolean((dateFrom && !dateTo) || (!dateFrom && dateTo));
    const hasCompleteDateRange = Boolean(dateFrom && dateTo);
    const hasInvalidDateRange = hasCompleteDateRange && dateFrom > dateTo;
    const hasFilterValues = Boolean(
        hasActiveFilters ||
        miplLocationId ||
        fuelPumpId ||
        dateFrom ||
        dateTo
    );
    const canApplyFilters =
        Boolean(miplLocationId || fuelPumpId || hasCompleteDateRange) &&
        !hasPartialDateRange &&
        !hasInvalidDateRange;

    const applyFilters = () => {
        setAppliedMiplLocationId(miplLocationId.trim());
        setAppliedFuelPumpId(fuelPumpId.trim());
        setAppliedDateFrom(dateFrom);
        setAppliedDateTo(dateTo);
        setFilterOpen(false);
        setFilterSub(null);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setMiplLocationId('');
        setFuelPumpId('');
        setDateFrom('');
        setDateTo('');
        setAppliedMiplLocationId('');
        setAppliedFuelPumpId('');
        setAppliedDateFrom('');
        setAppliedDateTo('');
        setLocationSearch('');
        setPumpSearch('');
        setFilterOpen(false);
        setFilterSub(null);
        setCurrentPage(1);
    };

    const handleLocationSelect = (id) => {
        setMiplLocationId(id);
        setFuelPumpId('');
        setPumpSearch('');
        setFilterSub(null);
    };

    const handlePumpSelect = (id) => {
        setFuelPumpId(id);
        setFilterSub(null);
    };

    const filteredLocations = filterByQuery(locations, locationSearch);
    const filteredPumps = filterByQuery(fuelPumps, pumpSearch);
    const locForPumps = miplLocationId || appliedMiplLocationId;

    return (
        <>
            <div className="tab-pane-content mt-2.5 md:mt-7">
                <div className="md:mb-4 mb-2.5">
                    <div className='flex flex-wrap items-center gap-2.5'>
                        <input
                           type="search"
                            placeholder="Search"
                            className="searchIcon min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:!shadow-sm sm:max-w-[min(100%,28rem)]"
                            value={search}
                            maxLength={SEARCH_MAX_LENGTH}

                            onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                        />
                        <div className='max-[767px]:hidden'>
                            <DateRangeAppliedLabel
                                from={appliedDateFrom}
                                to={appliedDateTo}
                                prefix="Date range: "
                            />
                            {hasActiveFilters && (
                                <button
                                    type="button"
                                    onClick={clearFilters}
                                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-UbuntuMedium text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                        <div className="relative flex-shrink-0" ref={filterRef}>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilterOpen((o) => !o);
                                    setFilterSub(null);
                                }}
                                className="inline-flex md:h-[42px] md:w-[42px] h-[36px] w-[36px] items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
                                aria-label="Filters"
                            >
                                <img
                                    src={FilterIcon}
                                    alt=""
                                    className="md:h-5 md:w-5 h-4 w-4 invert grayscale brightness-100"
                                />
                            </button>
                            {filterOpen && (
                                <div className="absolute left-0 z-50 mt-2 min-w-[240px] rounded-lg border border-primary/25 bg-white py-2 shadow-lg sm:left-auto sm:right-0 max-[767px]:right-0 max-[767px]:left-auto">
                                    <button
                                        type="button"
                                        className="flex w-full items-center justify-between px-4 py-2 text-left text-sm text-[#030253] hover:bg-primary/5"
                                        onClick={() =>
                                            setFilterSub(filterSub === 'location' ? null : 'location')
                                        }
                                    >
                                        Location
                                        <FilterChevron open={filterSub === 'location'} />
                                    </button>
                                    {filterSub === 'location' && (
                                        <div className="border-t border-gray-100 px-3 py-2">
                                            <input

                                                type="search"
                                                placeholder="Search"
                                                value={locationSearch}
                                                maxLength={SEARCH_MAX_LENGTH}

                                                onChange={(e) => setLocationSearch(clampSearchInput(e.target.value))}
                                                className="searchIcon mb-2 w-full rounded border border-primary/30 px-2 py-1.5 text-sm outline-none focus:border-primary"
                                            />
                                            <div className="max-h-40 overflow-y-auto">
                                                {locationsLoading ? (
                                                    <p className="mb-0 px-1 py-2 text-xs text-gray-500">
                                                        Loading…
                                                    </p>
                                                ) : filteredLocations.length === 0 ? (
                                                    <p className="mb-0 px-1 py-2 text-xs text-gray-500">
                                                        No locations found
                                                    </p>
                                                ) : (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={`block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-primary/5 ${
                                                                !miplLocationId
                                                                    ? 'font-UbuntuMedium text-primary'
                                                                    : 'text-gray-800'
                                                            }`}
                                                            onClick={() => handleLocationSelect('')}
                                                        >
                                                            All locations
                                                        </button>
                                                        {filteredLocations.map((loc) => (
                                                            <button
                                                                key={loc.id}
                                                                type="button"
                                                                className={`block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-primary/5 ${
                                                                    miplLocationId === loc.id
                                                                        ? 'font-UbuntuMedium text-primary'
                                                                        : 'text-gray-800'
                                                                }`}
                                                                onClick={() =>
                                                                    handleLocationSelect(loc.id)
                                                                }
                                                            >
                                                                {loc.name}
                                                            </button>
                                                        ))}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="flex w-full items-center justify-between border-t border-gray-100 px-4 py-2 text-left text-sm text-[#030253] hover:bg-primary/5"
                                        onClick={() => setFilterSub(filterSub === 'pump' ? null : 'pump')}
                                    >
                                        Pump
                                        <FilterChevron open={filterSub === 'pump'} />
                                    </button>
                                    {filterSub === 'pump' && (
                                        <div className="border-t border-gray-100 px-3 py-2">
                                            {!locForPumps ? (
                                                <p className="mb-0 px-1 py-2 text-xs text-amber-700">
                                                    Select a location first
                                                </p>
                                            ) : (
                                                <>
                                                    <input

                                                        type="search"
                                                        placeholder="Search"
                                                        value={pumpSearch}
                                                        maxLength={SEARCH_MAX_LENGTH}

                                                        onChange={(e) => setPumpSearch(clampSearchInput(e.target.value))}
                                                        className="searchIcon mb-2 w-full rounded border border-primary/30 px-2 py-1.5 text-sm outline-none focus:border-primary"
                                                    />
                                                    <div className="max-h-40 overflow-y-auto">
                                                        {pumpsLoading ? (
                                                            <p className="mb-0 px-1 py-2 text-xs text-gray-500">
                                                                Loading…
                                                            </p>
                                                        ) : filteredPumps.length === 0 ? (
                                                            <p className="mb-0 px-1 py-2 text-xs text-gray-500">
                                                                No pumps found
                                                            </p>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className={`block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-primary/5 ${
                                                                        !fuelPumpId
                                                                            ? 'font-UbuntuMedium text-primary'
                                                                            : 'text-gray-800'
                                                                    }`}
                                                                    onClick={() => handlePumpSelect('')}
                                                                >
                                                                    All pumps
                                                                </button>
                                                                {filteredPumps.map((pump) => (
                                                                    <button
                                                                        key={pump.id}
                                                                        type="button"
                                                                        className={`block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-primary/5 ${
                                                                            fuelPumpId === pump.id
                                                                                ? 'font-UbuntuMedium text-primary'
                                                                                : 'text-gray-800'
                                                                        }`}
                                                                        onClick={() =>
                                                                            handlePumpSelect(pump.id)
                                                                        }
                                                                    >
                                                                        {pump.name}
                                                                    </button>
                                                                ))}
                                                            </>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="flex w-full items-center justify-between border-t border-gray-100 px-4 py-2 text-left text-sm text-[#030253] hover:bg-primary/5"
                                        onClick={() => setFilterSub(filterSub === 'dates' ? null : 'dates')}
                                    >
                                        Date Range
                                        <FilterChevron open={filterSub === 'dates'} />
                                    </button>
                                    {filterSub === 'dates' && (
                                        <div className="border-t border-gray-100 px-4 py-3">
                                            <DateRangeFilter
                                                variant="embedded"
                                                from={dateFrom}
                                                to={dateTo}
                                                onChange={({ from, to }) => {
                                                    setDateFrom(from);
                                                    setDateTo(to);
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="flex gap-2 border-t border-gray-100 px-4 py-2">
                                        {hasFilterValues && (
                                            <button
                                                type="button"
                                                className="btn w-full !rounded-lg !border-gray-300 !bg-white !py-2 text-sm !text-gray-800 hover:!bg-gray-50"
                                                onClick={clearFilters}
                                            >
                                                Clear
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-primary w-full !rounded-lg !py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                                            disabled={!canApplyFilters}
                                            onClick={applyFilters}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='text-center mt-2 [&_.h-11]:h-auto flex items-cener justify-between [&_.inline-flex]:text-xs md:hidden'>
                        <DateRangeAppliedLabel
                            from={appliedDateFrom}
                            to={appliedDateTo}
                            prefix="Date range: "
                        />
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-UbuntuMedium text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>

                <div className="my-6 grid md:grid-cols-12 grid-cols-2 items-end md:gap-4 gap-2 font-UbuntuRegular">
                    <div className="md:col-span-3 rounded-md bg-white !p-4 md:!p-4 shadow-sm">
                        <h2 className="md:text-2xl text-xl font-bold">{stats.totalVehicle}</h2>
                        <p className="mt-2 text-sm font-UbuntuMedium text-gray-500">No. Of Vehicle</p>
                    </div>
                    <div className="md:col-span-3 rounded-md bg-white !p-4 md:!p-4 shadow-sm">
                        <h2 className="md:text-2xl text-xl font-bold">{stats.totalFuel}</h2>
                        <p className="mt-2 text-sm font-UbuntuMedium text-gray-500">Diesel</p>
                    </div>
                    <div className="col-span-4 md:col-span-3 rounded-md bg-white !p-4 md:!p-4 shadow-sm">
                        <h2 className="md:text-2xl text-xl font-bold">{stats.totalAmount}</h2>
                        <p className="mt-2 text-sm font-UbuntuMedium text-gray-500">Fuel Amount</p>
                    </div>
                </div>

                <div className="common-table-wrapper m-0">
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Fuel Pump Name</th>
                                    <th>MIPL Location</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {listLoading ? (
                                    <tr>
                                        <td colSpan={4} className="py-6 text-center text-gray-500">
                                            Loading…
                                        </td>
                                    </tr>
                                ) : rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="py-6 text-center text-gray-500">
                                            No fuel pumps found
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row, index) => (
                                        <tr key={row.id}>
                                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td>{row.fuelPumpName}</td>
                                            <td>{row.miplLocation}</td>
                                            <td>
                                                <div className="flex items-center justify-center gap-2">
                                                    <Link
                                                        to={`/fuel-pump-management/view-settlement-details?fuel_pump_id=${encodeURIComponent(row.id)}&fuel_pump_name=${encodeURIComponent(row.fuelPumpName)}&mipl_location=${encodeURIComponent(row.miplLocation)}`}
                                                    >
                                                        <img
                                                            src={EyeIcon}
                                                            className="h-7 min-w-[22px]"
                                                            alt="View"
                                                        />
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-4 flex items-center gap-x-4">
                    <select
                        className="rounded border border-primary/30 p-2 text-sm shadow-sm outline-none focus:border-primary"
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                    <div className="text-xs md:text-sm text-gray-500 ml-auto">{totalRecords} records found</div>
                    <div className="flex items-center gap-2 md:pr-8">
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1 || listLoading}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage === 1 ? '' : 'cursor-pointer'}`}
                        >
                            <img
                                src={LeftAngle}
                                className="invert brightness-100 grayscale"
                                alt="Previous"
                            />
                        </button>
                        <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                        <span className="text-sm text-gray-600 text-nowrap">of {totalPages}</span>
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages || listLoading}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center  ${currentPage === totalPages ? '' : 'cursor-pointer'}`}
                        >
                            <img
                                src={RightAngle}
                                className="invert brightness-100 grayscale"
                                alt="Next"
                            />
                        </button>
                    </div>
                </div>
            </div>
            <Toastify />
        </>
    );
};

export default FuelPumpManagement;
